import { useEffect, useRef, type ReactNode } from 'react';

interface ModalBaseProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  statusLabel?: string;
  statusColor?: string;
  children: ReactNode;
  footer?: ReactNode;
  footerCritical?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  headerIcon?: ReactNode;
  headerBorder?: boolean;
  footerBorder?: boolean;
}

const sizeClasses: Record<string, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
};

export default function ModalBase({
  open,
  onClose,
  title,
  subtitle,
  statusLabel,
  statusColor,
  children,
  footer,
  footerCritical,
  size = 'md',
  headerIcon,
  headerBorder = true,
  footerBorder = true,
}: ModalBaseProps) {
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm"></div>

      {/* Modal panel — mobile: bottom sheet, desktop: centered */}
      <div
        className={`relative bg-background-50 w-full ${sizeClasses[size]} mx-auto shadow-xl
          rounded-t-2xl sm:rounded-2xl
          max-h-[92vh] sm:max-h-[85vh]
          overflow-hidden flex flex-col
          animate-fade-in-up`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`shrink-0 px-5 py-4 flex items-start justify-between gap-4 ${headerBorder ? 'border-b border-background-200' : ''}`}>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-0.5">
              {headerIcon && (
                <div className="shrink-0">
                  {headerIcon}
                </div>
              )}
              <h3 className="text-foreground-950 font-bold text-sm truncate">{title}</h3>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {subtitle && (
                <p className="text-foreground-400 text-[10px] leading-relaxed">{subtitle}</p>
              )}
              {statusLabel && (
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold ${statusColor || 'bg-background-100 text-foreground-500'}`}>
                  {statusLabel}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors shrink-0"
            aria-label="Cerrar"
          >
            <i className="ri-close-line text-foreground-500 text-sm"></i>
          </button>
        </div>

        {/* Content — scrollable */}
        <div className="flex-1 overflow-y-auto px-5 py-5">
          {children}
        </div>

        {/* Footer */}
        {(footer || footerCritical) && (
          <div className={`shrink-0 px-5 py-4 space-y-3 ${footerBorder ? 'border-t border-background-200' : ''}`}>
            {footerCritical && (
              <div className="flex items-center gap-2 justify-end flex-wrap">
                {footerCritical}
              </div>
            )}
            {footer && footerCritical && (
              <div className="border-t border-background-200/70 pt-3 flex items-center gap-2 justify-end flex-wrap">
                {footer}
              </div>
            )}
            {footer && !footerCritical && (
              <div className="flex items-center gap-2 justify-end flex-wrap">
                {footer}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}